import React, { useRef, useMemo, useEffect } from 'react'
import Intercom, { update, shutdown } from '@intercom/messenger-js-sdk'
import { useSelector, shallowEqual } from 'react-redux'

const appId = process.env.REACT_APP_INTERCOM_APP_ID

const selectIntercomIdentity = (state) => {
	const { isAuthenticated, user, intercomToken } = state.auth ?? {}
	return {
		isAuthenticated: Boolean(isAuthenticated && user),
		intercomToken: intercomToken ?? null,
		user: user ?? null,
	}
}

export const IntercomProvider = ({ children }) => {
	const identity = useSelector(selectIntercomIdentity, shallowEqual)
	const hasBootedRef = useRef(false)
	const lastPayloadRef = useRef(null)

	const isAuthenticated = identity.isAuthenticated
	const intercomToken = identity.intercomToken
	const userId = identity.user?._id ?? null
	const userType = identity.user?.__t ?? null
	const businessName = identity.user?.businessName ?? null
	const preferredName = identity.user?.preferredName ?? null
	const firstName = identity.user?.firstName ?? null
	const lastName = identity.user?.lastName ?? null

	const userPayload = useMemo(() => {
		if (!isAuthenticated || !intercomToken) return null

		const isVendorOrPlanner = ['vendor', 'kazoo-planner'].includes(userType)
		const isUser = ['user'].includes(userType)

		const payload = {
			intercom_user_jwt: intercomToken,
			user_id: userId,
			otb_platform_role: userType,
		};

		if (isVendorOrPlanner && businessName) {
			payload.company = { company_id: userId, name: businessName }
		}

		if (isUser) {
			const fullName = [firstName, lastName].filter(Boolean).join(' ')
			const name = preferredName ?? fullName ?? null
			if (name) payload.name = name
		}

		return payload
	}, [
		isAuthenticated,
		intercomToken,
		userId,
		userType,
		businessName,
		preferredName,
		firstName,
		lastName,
	])

	useEffect(() => {
		if (typeof window !== 'undefined' && window.Cypress) return
		if (!appId) return

		const serialized = JSON.stringify(
			userPayload,
			Object.keys(userPayload ?? {}).sort()
		)
		if (serialized === lastPayloadRef.current) return
		lastPayloadRef.current = serialized

		if (!userPayload) {
			if (hasBootedRef.current) {
				shutdown()
				hasBootedRef.current = false
			}
			Intercom({ app_id: appId })
			hasBootedRef.current = true
			return
		}

		if (!hasBootedRef.current) {
			Intercom({ app_id: appId, ...userPayload })
			hasBootedRef.current = true
		} else {
			update({ ...userPayload })
		}
	}, [userPayload])

	useEffect(() => {
		return () => {
			if (hasBootedRef.current) {
				shutdown()
				hasBootedRef.current = false
			}
		}
	}, [])

	return <>{children}</>
}
